import React, { useState, useEffect } from "react";
import AvatarImg from "../../../../images/profile-avatar.png";
import {
  DevicePhoneMobileIcon,
  ArrowLeftIcon,
  EnvelopeIcon,
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import {
  formatAddress,
  formatDate,
  formatPhoneNumber,
  generateFullImageUrl,
} from "../../../../utilities/utils";
import Rating from "../../../rating";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import IconButton from "../../../iconButton";
import { useGetMatchDetailsQuery } from "../../../../store/admin/matchesInfo";
import Loader from "../../../commons/Loader";
import { getIsMHCName } from "../../../../utilities/utils";

const AllMatchesDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const { data: matchDetails } = useGetMatchDetailsQuery(id);
  const matchInfo = matchDetails?.data ?? {};

  const hasDrPrefix = matchInfo?.user?.professions.some(
    (profession) => profession.add_dr_prefix === 1
  );

  const [answerQuestionsData, setAnswerQuestionsData] = useState([]);

  useEffect(() => {
    if (matchInfo?.lead?.answers) {
      let questionAnswers = getUniqueData(matchInfo?.lead?.answers);
      setAnswerQuestionsData(questionAnswers);
    }
  }, [matchInfo]);

  const isMHC =
    matchInfo?.user?.professions[0]?.name == "Mental Health Counselor";

  function getUniqueData(array) {
    let updatedArray = [];
    array.forEach((element) => {
      let foundIndex = updatedArray.findIndex(
        (updArr) => updArr.question.id === element.question.id
      );
      if (foundIndex != -1) {
        let tempObject = { ...updatedArray[foundIndex] };
        tempObject.option_id = [
          updatedArray[foundIndex].option_id,
          element.option_id,
        ];
        updatedArray[foundIndex] = tempObject;
      } else {
        updatedArray.push(element);
      }
    });
    return updatedArray;
  }

  const getAnswer = (answer, answerOptionId) => {
    if (!answer?.question?.options) return answer?.content ?? "";

    const foundOption = answer.question.options.filter((option) =>
      Array.isArray(answerOptionId)
        ? answerOptionId.includes(option.id)
        : option.id === answerOptionId
    );

    return foundOption.length
      ? foundOption.map((opt) => opt.text).join(" , ")
      : answer?.content ?? "";
  };

  const leadServicesNames =
    matchInfo &&
    matchInfo?.lead?.services
      ?.map((services) => {
        return services;
      })
      .flat();

  const professionNames =
    matchInfo &&
    matchInfo?.user?.professions
      ?.map((profession) => {
        return profession.name;
      })
      .flat();

  const firstThreeServices = leadServicesNames?.length
    ? leadServicesNames
        .slice(0, Math.min(leadServicesNames.length, 3))
        .map((service) => service.name)
        .join(leadServicesNames.length > 2 ? "\ " : " and ")
    : "";

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleViewProfile = () => {
    navigate(`/admin-dashboard/users/${matchInfo?.user.id}`, {
      state: {
        userInfo: matchInfo?.user,
      },
    });
  };

  return (
    <>
      {!matchDetails ? (
        <Loader isLoading={true} />
      ) : (
        <div className="col-span-12">
          <div className="flex items-center gap-[6px] xl:gap-[10px] mb-5 xl:mb-[30px]">
            <div className="flex items-center gap-[10px]">
              <ArrowLeftIcon
                className="w-6 h-6 text-[#485868] cursor-pointer"
                onClick={handleGoBack}
              />
              <h3
                className="text-xl xl:text-2xl font-normal leading-[130%] text-[#485868] cursor-pointer"
                onClick={handleGoBack}
              >
                Matches
              </h3>
            </div>
            <span className="text-[#485868]">&#8226;</span>
            <h3 className="text-xl xl:text-2xl font-bold leading-[130%] text-customGray">
              {matchInfo.lead?.first_name.concat(
                " ",
                matchInfo.lead.last_name ?? ""
              )}
            </h3>
          </div>
          <div className="border border-[#EBEFF3] rounded-md bg-white p-5 mb-6">
            <h6 className="text-base md:text-lg font-bold leading-[160%] text-[#04091E] mb-[14px] md:mb-5 capitalize">
              Matched Professional
            </h6>
            <div className="grid grid-cols-1 4xl:grid-cols-2 gap-6 3xl:gap-10 max-w-full">
              <div className="">
                <div className="flex gap-5 mb-5 md:mb-0">
                  <div className="relative w-[90px] md:w-[115px] h-[90px] md:h-[115px] rounded-[50%]">
                    <img
                      src={
                        matchInfo?.user?.photo
                          ? generateFullImageUrl(matchInfo?.user?.photo)
                          : AvatarImg
                      }
                      alt=""
                      className="w-[90px] md:w-[115px] h-[90px] md:h-[115px] rounded-[50%] bg-[#485868]"
                    />
                    <div className="absolute right-1 md:right-2 bottom-1 md:bottom-2 w-4 md:w-5 h-4 md:h-5">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <circle
                          cx="10"
                          cy="10"
                          r="9"
                          fill="#16CA7C"
                          stroke="white"
                          strokeWidth="2"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="w-[calc(100%-110px)] md:w-[calc(100%-135px)]">
                    <div className="flex justify-between">
                      <div>
                        <div className="flex items-center gap-4">
                          <h4 className="text-base xl:text-xl font-bold leading-[180%] text-[#04091E]">
                            {hasDrPrefix && "Dr."}{" "}
                            {matchInfo?.user?.first_name
                              ? matchInfo?.user?.first_name.concat(" ")
                              : ""}
                            {matchInfo?.user?.last_name
                              ? matchInfo?.user?.last_name.concat(" ")
                              : ""}
                            {matchInfo?.user?.title
                              ? matchInfo?.user?.title
                              : ""}
                          </h4>
                          <div className="hidden md:flex items-center gap-0.5">
                            <Rating rating={matchInfo?.user?.rating ?? ""} />
                          </div>
                        </div>
                        <span className="text-sm xl:text-base font-normal leading-[160%] text-[#485868] inline-block mb-[10px]">
                          {/* {professionNames?.join(", ") || "---"} */}
                          {isMHC
                            ? getIsMHCName(matchInfo?.user?.professions[0])
                            : professionNames?.join(", ") || "---"}
                        </span>
                        <div className="flex md:hidden items-center">
                          <Rating rating={matchInfo?.user?.rating ?? ""} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="md:pl-[135px] md:-mt-8">
                  <div className="flex items-start 4xl:items-center flex-col 4xl:flex-row gap-3 4xl:gap-[30px] mb-3">
                    <a
                      href={`tel:${
                        matchInfo?.user?.office_phone_number || "0000000000"
                      }`}
                      className="text-sm font-medium leading-[160%] text-customGray flex items-center gap-1.5"
                    >
                      <span>
                        <DevicePhoneMobileIcon className="w-5 h-5 text-primary" />
                      </span>
                      {(matchInfo &&
                        formatPhoneNumber(
                          matchInfo?.user?.office_phone_number
                        )) ||
                        "(561) 425-7033"}
                    </a>
                    <a
                      href={`mailto:${matchInfo?.user?.email}`}
                      className="text-sm font-medium leading-[160%] text-customGray flex items-center gap-1.5"
                      style={{ lineBreak: "anywhere" }}
                    >
                      <span>
                        <EnvelopeIcon className="w-5 h-5 text-primary" />
                      </span>
                      {matchInfo?.user?.email}
                    </a>
                  </div>
                </div>
              </div>
              <div className="4xl:pl-[30px] 4xl:border-l 4xl:border-[#EBEFF3] max-w-[500px] 4xl:max-w-full">
                <IconButton
                  title="View Profile"
                  icon={UserIcon}
                  iconClasses="w-6 h-6"
                  classes="text-sm font-bold leading-[160%] text-primary capitalize flex items-center justify-center gap-1.5 rounded-[10px] bg-[#FFFFFF] h-12 max-w-[305px] w-full 3xl:w-[138px] border border-primary mb-5"
                  onClick={() => handleViewProfile()}
                />
                {matchInfo?.user?.profile_percentage == "100" && (
                  <div className="bg-[#F1F4F6] rounded-[3px] py-2 px-[14px] flex items-center justify-between gap-5">
                    <span className="text-xs font-bold leading-[160%] text-customGray uppercase max-w-[200px] w-full">
                      Professional’s <br /> Achievements:
                    </span>
                    <ul className="flex items-center gap-2.5 max-w-[calc(100%-220px)] w-full">
                      <li></li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <div className="border border-solid border-[#EBEFF3] rounded-md p-5 bg-[#ffffff]">
              <h5 className="text-sm xl:text-lg font-bold leading-[160%] text-customGray mb-[10px] xl:mb-5">
                Lead Information
              </h5>
              <ul className="lead-info">
                <li className="flex items-center gap-6 xl:gap-[34px] py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[156px] flex items-center gap-[10px] xl:gap-3 text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    <UserIcon className="w-5 xl:w-6 h-5 xl:h-6 text-customGray" />
                    Name
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-200px)] w-full">
                    {matchInfo?.lead?.first_name.concat(
                      " ",
                      matchInfo?.lead?.last_name
                    )}
                  </span>
                </li>
                <li className="flex items-center gap-6 xl:gap-[34px] py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[156px] flex items-center gap-[10px] xl:gap-3 text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    <PhoneIcon className="w-5 xl:w-6 h-5 xl:h-6 text-customGray" />
                    Phone Number
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-200px)] w-full">
                    {formatPhoneNumber(matchInfo?.lead?.phone_number) || "---"}
                  </span>
                </li>
                <li className="flex items-center gap-6 xl:gap-[34px] py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[156px] flex items-center gap-[10px] xl:gap-3 text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    <EnvelopeIcon className="w-5 xl:w-6 h-5 xl:h-6 text-customGray" />
                    Email
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-200px)] w-full">
                    {matchInfo?.lead?.email || "---"}
                  </span>
                </li>
                <li className="flex items-center gap-6 xl:gap-[34px] py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[156px] flex items-center gap-[10px] xl:gap-3 text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    <MapPinIcon className="w-5 xl:w-6 h-5 xl:h-6 text-customGray" />
                    Location
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-200px)] w-full">
                    {matchInfo?.lead?.city
                      ? matchInfo?.lead?.city.concat(", ")
                      : ""}
                    {matchInfo?.lead?.county
                      ? matchInfo?.lead?.county.concat(" ")
                      : ""}
                    {matchInfo?.lead?.postal_code
                      ? matchInfo?.lead?.postal_code
                      : ""}
                  </span>
                </li>
                <li className="flex items-center gap-6 xl:gap-[34px] py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[156px] flex items-center gap-[10px] xl:gap-3 text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    <CalendarDaysIcon className="w-5 xl:w-6 h-5 xl:h-6 text-customGray" />
                    Created at:
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-200px)] w-full">
                    {formatDate(matchInfo?.lead?.created_at)}
                  </span>
                </li>
              </ul>
            </div>
            <div className="border border-solid border-[#EBEFF3] rounded-md p-5 bg-[#ffffff]">
              <h5 className="text-sm xl:text-lg font-bold leading-[160%] text-customGray mb-[10px] xl:mb-5">
                Match Details
              </h5>
              <ul className="lead-info">
                <li className="flex items-center gap-6 py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[144px] text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    Profession
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-168px)] w-full">
                    {/* {matchInfo?.lead?.profession?.name || "---"} */}
                    {isMHC
                      ?  getIsMHCName(matchInfo?.user?.professions[0])
                      : professionNames?.join(", ") || "---"}
                  </span>
                </li>
                <li className="flex items-center gap-6 py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[144px] text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    Credit's Charged
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-168px)] w-full">
                    {matchInfo?.credit_cost ? matchInfo?.credit_cost : "---"}
                  </span>
                </li>
                <li className="flex items-center gap-6 py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[144px] text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    {isMHC ? "Specialization" : "Services"}
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-168px)] w-full">
                    {isMHC
                      ?  getIsMHCName(matchInfo.lead?.profession)
                      : firstThreeServices}
                    {!showMore&& leadServicesNames?.length>3 ? "," : ""}
                    {leadServicesNames?.length > 3 && !showMore && (
                      <button
                        className="text-primary inline"
                        onClick={() => setShowMore(true)}
                      >
                        +More Listed Below
                      </button>
                    )}
                  </span>
                </li>
                {isMHC ? (
                  <li className="flex items-center gap-6 py-[10px] px-[10px] xl:px-4">
                    <h6 className="w-[130px] xl:w-[144px] text-sm xl:text-base font-bold leading-[160%] text-customGray">
                      Primary Reason for Therapy
                    </h6>
                    <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-168px)] w-full">
                      {firstThreeServices}
                    </span>
                  </li>
                ) : (
                  ""
                )}
                <li className="flex items-center gap-6 py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[144px] text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    Location
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-168px)] w-full">
                    {matchInfo?.user?.city
                      ? matchInfo?.user?.city.concat(", ")
                      : ""}
                    {matchInfo?.user?.county
                      ? matchInfo?.user?.county.concat(" ")
                      : ""}
                    {matchInfo?.user?.postal_code
                      ? matchInfo?.user?.postal_code
                      : ""}
                  </span>
                </li>
                <li className="flex items-center gap-6 py-[10px] px-[10px] xl:px-4">
                  <h6 className="w-[130px] xl:w-[144px] text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    Match Created:
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray max-w-[calc(100%-154px)] xl:max-w-[calc(100%-168px)] w-full">
                    {formatDate(matchInfo?.created_at)}
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border border-solid border-[#EBEFF3] rounded-md p-5 bg-[#ffffff]">
            <h5 className="text-sm xl:text-lg font-bold leading-[160%] text-customGray">
              Questions Answered
            </h5>
            <ul className="ques-ans px-5">
              {answerQuestionsData &&
                answerQuestionsData?.map((answer) => {
                  let questionsData = answer?.question;
                  return (
                    <li className="grid grid-cols-1 xl:grid-cols-2 gap-[10px] xl:gap-6 py-[14px] xl:py-5 border-b border-solid border-[#EBEFF3]">
                      <h6 className="text-sm xl:text-base font-bold leading-[160%] text-customGray">
                        {questionsData?.text ?? ""}
                      </h6>
                      <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray">
                        {getAnswer(answer, answer.option_id)}
                      </span>
                    </li>
                  );
                })}
              {/* {matchInfo?.user?.insuranceCarriers?.length > 0 && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-[10px] xl:gap-6 py-[14px] xl:py-5 border-b border-solid border-[#EBEFF3]">
                  <h6 className="text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    Do you currently have insurance?
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray">
                    {matchInfo?.user?.insuranceCarriers
                      ? `Yes (${matchInfo?.user?.insuranceCarriers
                          .map((carrier) => carrier.name)
                          .join(", ")})`
                      : "No"}
                  </span>
                </div>
              )} */}
              {matchInfo?.about && (
                <div
                  className={`grid grid-cols-1 xl:grid-cols-2 gap-[10px] xl:gap-6 py-[14px] xl:py-5 ${
                    showMore && "border-b border-solid border-[#EBEFF3]"
                  }`}
                >
                  <h6 className="text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    Additional information provided:
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray">
                    {matchInfo && matchInfo?.about}
                  </span>
                </div>
              )}
              {showMore && (
                <li className="grid grid-cols-1 xl:grid-cols-2 gap-[10px] xl:gap-6 py-[14px] xl:py-5 border-b border-solid border-[#EBEFF3]">
                  <h6 className="text-sm xl:text-base font-bold leading-[160%] text-customGray">
                    All Services Requested
                  </h6>
                  <span className="text-sm xl:text-base font-normal leading-[160%] text-customGray">
                    {leadServicesNames
                      ?.map((service) => {
                        return service.name;
                      })
                      .join(", ")}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default AllMatchesDetailPage;
